import React, { useState, useEffect, useRef, useCallback } from "react";
import env from "@beam-australia/react-env";
import { useTranslation } from "react-i18next";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { BlockUI } from "primereact/blockui";

import Layout from "../../../../layout/layout";

import useApi from "../../../../hooks/useApi";
import useCRUDService from "../../../../hooks/useCRUDService";
import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats, { formatDecimal } from "../../../../utils/helperFormats";

import "../../../../datatable.css";

const Applications = () => {
  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("applications");

  const [entities, setEntities] = useState(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const searchInput = useRef(null);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "createdDate",
          type: "date",
        },
        {
          name: "amount",
          type: "number",
        },
        {
          name: "paymentsNumber",
          type: "number",
        },
        {
          name: "paymentsAmount",
          type: "number",
        },
      ]);

      const response = await api.get(
        `${env("API_URL")}/website/applications/paged`,
        {
          params: {
            columns,
            page: first,
            pageSize: rows,
            sort: sortField ?? "createdDate",
            order: sortOrder >= 0 ? "ASC" : "DESC",
          },
        }
      );

      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [first, rows, sortField, sortOrder, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [first, rows, sortField, sortOrder]);

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary buttonExport"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  return (
    <React.Fragment>
      <Layout>
        <div className="flex flex-grow-1 flex-column w-screen h-full">
          <div className="flex flex-column">
            <div className="flex justify-content-center">
              <h2 className="pageTitle">{t("website.applications.title")}</h2>
            </div>
            <div className="flex justify-content-center">
              <p>{t("website.applications.subTitle")}</p>
            </div>
          </div>
          <div className="flex justify-content-center">
            <DataTable
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              onSort={(e) => {
                setSortField(e.sortField);
                setSortOrder(e.sortOrder);
              }}
              sortField={sortField}
              sortOrder={sortOrder}
              ref={dt}
              value={entities}
              paginator
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              columnResizeMode="fit"
              showGridlines
              header={header}
              dataKey="id"
              rowsPerPageOptions={[10, 25, 100, 1000]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate={`${t(
                "DataTable.currentPageReportTemplate"
              )} ${t("entities.applications.plural")}`}
              globalFilter={globalFilter}
              className="custom-table w-8"
              emptyMessage={t("DataTable.emptyMessage")}
            >
              <Column
                field="createdDate"
                header={t("entities.applications.createdDate")}
                bodyStyle={{ textAlign: "center" }}
                body={(rowData) =>
                  helperFormats.formatDate(new Date(rowData.createdDate))
                }
              ></Column>
              <Column
                field="amount"
                header={t("entities.applications.amount")}
                bodyStyle={{ textAlign: "right" }}
                body={(rowData) => helperFormats.formatDecimal(rowData.amount)}
              ></Column>
              <Column
                field="installmentsNumber"
                bodyStyle={{ textAlign: "center" }}
                header={t("entities.applications.installmentsNumber")}
              ></Column>
              <Column
                field="installmentsAmount"
                header={t("entities.applications.installmentsAmount")}
                bodyStyle={{ textAlign: "right" }}
                body={(rowData) =>
                  helperFormats.formatDecimal(rowData.installmentsAmount)
                }
              ></Column>
            </DataTable>
          </div>
        </div>
      </Layout>
    </React.Fragment>
  );
};

export default Applications;
